import { useEffect, useMemo, useState } from "react";
import { ClipboardList, X, Loader2, Sparkles, CheckSquare, Square, Send } from "lucide-react";
import api from "../../lib/api";
import { useSentinelBusy } from "../../lib/sentinel-busy";

const PRIORITY_COLOR = {
  high: "var(--red)",
  medium: "var(--amber)",
  low: "var(--text-2)",
};

const WEEK_LABELS = { 1: "Week 1", 2: "Week 2", 3: "Week 3", 4: "Week 4" };

function PlanRow({ item, checked, onToggle }) {
  const idx = `${item.trainee_id}::${item.course_id}`;
  return (
    <label
      htmlFor={idx}
      className="flex items-start gap-3 border border-[var(--border)] p-3 cursor-pointer hover:border-[var(--cyan)] transition-colors"
      data-testid={`plan-row-${item.trainee_id}-${item.course_code}`}
    >
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onToggle(idx); }}
        className="mt-0.5 shrink-0"
        aria-label={checked ? "Deselect" : "Select"}
      >
        {checked
          ? <CheckSquare size={18} color="var(--cyan)" strokeWidth={1.5} />
          : <Square size={18} color="var(--text-2)" strokeWidth={1.5} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-display font-bold uppercase text-sm">{item.trainee_name}</div>
            <div className="font-mono text-[10px] text-[var(--text-2)] tracking-widest uppercase mt-0.5">
              TRK / {item.trainee_track}
            </div>
          </div>
          <span
            className="font-mono text-[10px] px-2 py-0.5 border tracking-widest uppercase"
            style={{ color: PRIORITY_COLOR[item.priority], borderColor: PRIORITY_COLOR[item.priority] }}
          >
            {item.priority}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="font-mono text-xs text-[var(--cyan)]">{item.course_code}</span>
          <span className="font-display text-sm uppercase">{item.course_title}</span>
          <span className="font-mono text-[10px] text-[var(--text-2)]">
            · {item.course_stage} · {item.course_duration_hours}h
          </span>
        </div>
        {item.justification && (
          <p className="text-xs text-[var(--text-2)] mt-2 italic">{item.justification}</p>
        )}
      </div>
    </label>
  );
}

export default function PlanModal({ onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const { startBusy } = useSentinelBusy();

  useEffect(() => {
    let cancelled = false;
    const stopBusy = startBusy();
    (async () => {
      try {
        const res = await api.post("/squad/plan");
        if (cancelled) return;
        setData(res.data);
        // Auto-select all by default
        const all = new Set(res.data.plan.map((p) => `${p.trainee_id}::${p.course_id}`));
        setSelected(all);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
        stopBusy();
      }
    })();
    return () => { cancelled = true; stopBusy(); };
  }, [startBusy]);

  const grouped = useMemo(() => {
    const out = { 1: [], 2: [], 3: [], 4: [] };
    if (data?.plan) data.plan.forEach((p) => (out[p.week] || out[1]).push(p));
    return out;
  }, [data]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const apply = async () => {
    if (!data) return;
    const assignments = data.plan
      .filter((p) => selected.has(`${p.trainee_id}::${p.course_id}`))
      .map((p) => ({ trainee_id: p.trainee_id, course_id: p.course_id }));
    if (assignments.length === 0) return;
    setApplying(true);
    try {
      const res = await api.post("/squad/plan/apply", { assignments });
      setResult(res.data);
      onApplied?.(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="plan-modal"
    >
      <div
        className="w-full max-w-3xl flat-card p-6 scanlines relative max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white"
          data-testid="plan-close"
          aria-label="Close plan"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <ClipboardList size={22} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// SENTINEL_30_DAY_PLAN</div>
        </div>
        <h2 className="font-display font-bold text-2xl uppercase">Squad Training Plan</h2>
        <p className="font-mono text-xs text-[var(--text-2)] mt-1">
          Sentinel-prioritised assignments across the next 30 days.
        </p>

        {loading && (
          <div className="mt-8 flex items-center gap-3 text-[var(--cyan)] font-mono text-sm" data-testid="plan-loading">
            <Loader2 size={18} className="animate-spin" />
            <span className="blink">SENTINEL is planning the next 30 days █</span>
          </div>
        )}

        {err && (
          <div className="mt-6 border border-[var(--red)] p-3 font-mono text-xs text-[var(--red)]" data-testid="plan-error">
            {err}
          </div>
        )}

        {data && !result && (
          <div className="mt-5 flex-1 overflow-y-auto pr-2" data-testid="plan-content">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">
                {data.total_assignments} proposed · {selected.size} selected
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(new Set(data.plan.map((p) => `${p.trainee_id}::${p.course_id}`)))}
                  className="font-mono text-[10px] uppercase tracking-widest text-[var(--cyan)] hover:text-white"
                  data-testid="plan-select-all"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-2)] hover:text-white"
                  data-testid="plan-select-none"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="space-y-5">
              {[1, 2, 3, 4].map((w) => grouped[w]?.length > 0 && (
                <div key={w} data-testid={`plan-week-${w}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={12} color="var(--cyan)" />
                    <span className="font-mono text-xs tracking-widest uppercase text-[var(--cyan)]">{WEEK_LABELS[w]}</span>
                    <span className="font-mono text-[10px] text-[var(--text-2)]">· {grouped[w].length} task(s)</span>
                  </div>
                  <div className="space-y-2">
                    {grouped[w].map((item) => {
                      const key = `${item.trainee_id}::${item.course_id}`;
                      return (
                        <PlanRow
                          key={key}
                          item={item}
                          checked={selected.has(key)}
                          onToggle={toggle}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 border border-[var(--green)] p-4" data-testid="plan-result">
            <div className="font-display uppercase text-lg text-[var(--green)]">Plan Applied</div>
            <p className="text-sm text-[var(--text)] mt-1">
              Created <span className="text-[var(--cyan)] font-mono">{result.created}</span> enrollments,
              skipped <span className="text-[var(--amber)] font-mono">{result.skipped}</span> already-assigned.
            </p>
          </div>
        )}

        {data && !result && (
          <div className="flex gap-3 pt-4 mt-4 border-t border-[var(--border)]">
            <button
              onClick={apply}
              disabled={applying || selected.size === 0}
              className="btn-primary inline-flex items-center gap-2 flex-1 justify-center"
              data-testid="plan-apply"
            >
              <Send size={14} />
              {applying ? "Applying…" : `Apply ${selected.size} Assignment${selected.size === 1 ? "" : "s"}`}
            </button>
            <button onClick={onClose} className="btn-ghost" data-testid="plan-dismiss">
              Dismiss
            </button>
          </div>
        )}

        {result && (
          <div className="flex justify-end pt-4 mt-4 border-t border-[var(--border)]">
            <button onClick={onClose} className="btn-primary" data-testid="plan-done">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
